import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, Subject, Subscription, firstValueFrom } from 'rxjs';
import { MaintenanceOrderEnums } from 'src/app/enum/maintenance-order-enum';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { MaintenanceOrderNoteTableModel } from 'src/app/models/maintenance/maintenance-order-note-table.model';
import { MaintenanceOrderNoteModel } from 'src/app/models/maintenance/maintenance-order-note.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { MaintenanceOrderNoteService } from 'src/app/services/maintenance/maintenance-order-note.service';
import { ConceptService } from 'src/app/services/management/concept.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-note-stage-modal',
  templateUrl: './note-stage-modal.component.html',
  styleUrls: ['./note-stage-modal.component.scss'],
  standalone: false
})
export class NoteStageModalComponent implements OnInit {

  ascendant: boolean = true;
  changeLastNoteStage: boolean = false;
  columnSort: string | null = null;
  detailData: PagedResponseModel<MaintenanceOrderNoteTableModel> = new PagedResponseModel<MaintenanceOrderNoteTableModel>();
  formNoteStage: FormGroup = new FormGroup({});
  header: ColumnResponseModel[] = [];
  input$: BehaviorSubject<any>;
  limit: number = 10;
  listNoteStage: any[] = [];
  maintenanceOrderId: number = 0;
  maintenanceOrderNoteContract: MaintenanceOrderNoteModel = new MaintenanceOrderNoteModel();
  maintenanceOrderStateId: number = 0;
  output$: Subject<any>;
  page: number = 1;
  permissionUpdatable: boolean = false;
  suscription: Subscription;
  swBlock: boolean = false;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    }
  ];

  constructor(private conceptService: ConceptService,
    public config: DynamicDialogConfig,
    private dateConverterService: DateConverterService,
    private formBuilder: FormBuilder,
    private maintenanceOrderNoteService: MaintenanceOrderNoteService,
    private toastHandlerService: ToastHandlerService,
    @Optional() private ref: DynamicDialogRef,) {
    this.validateForm(this.maintenanceOrderNoteContract);
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.maintenanceOrderId = inputs.maintenanceOrderId;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.maintenanceOrderStateId = inputs.maintenanceOrderStateId;
      this.swBlock = inputs.swBlock;
    });
    await this.getAllLists();
    await this.getColumns();
    await this.getAllMaintenanceOrderNotesWithPagination();
  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  close() {
    const data: ModalObjectType = {};
    if (this.changeLastNoteStage) {
      data.params = { reload: true };
      this.ref.close(data);
    } else {
      data.params = { reload: false };
      this.ref.close(data);
    }
  }

  async getAllLists() {
    try {
      const [noteStages] = await Promise.all([
        firstValueFrom(this.conceptService.getAllConceptNoteStagesForSelectAsync()),
      ]);
      this.listNoteStage = noteStages

      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  async getAllMaintenanceOrderNotesWithPagination() {
    try {
      this.detailData = await firstValueFrom(
        this.maintenanceOrderNoteService.getAllMaintenanceOrderNotesWithPagination(this.maintenanceOrderId, this.page, this.limit, this.columnSort, this.ascendant, []));
    } catch (error) {
      console.error('Error al obtener las Notas / Etapas.', error);
    }
  }

  async getColumns() {
    try {
      this.header = await firstValueFrom(this.maintenanceOrderNoteService.getColumnsForGeneralConsultation());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async newNoteStage() {
    if (this.maintenanceOrderStateId === MaintenanceOrderEnums.cierreFinal || this.swBlock) {
      this.toastHandlerService.showToast('error', 'No se puede agregar una nota de etapa a una orden de mantenimiento en cierre final o bloqueada.');
      return;
    }
    const canUpdate = await this.validatePermissionUpdatable();
    if (!canUpdate) return;
    this.maintenanceOrderNoteContract = new MaintenanceOrderNoteModel();
    this.maintenanceOrderNoteContract.maintenanceOrderId = this.maintenanceOrderId;
    this.validateForm(this.maintenanceOrderNoteContract);
  }

  async onAction(event: { action: string, item?: MaintenanceOrderNoteTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      if (this.maintenanceOrderStateId === MaintenanceOrderEnums.cierreFinal || this.swBlock) {
        this.toastHandlerService.showToast('error', 'No se puede editar una nota de etapa en una orden de mantenimiento en cierre final o bloqueada.');
        return;
      }
      try {
        const canUpdate = await this.validatePermissionUpdatable();
        if (!canUpdate) return;
        this.maintenanceOrderNoteContract = await firstValueFrom(this.maintenanceOrderNoteService.getMaintenanceOrderNoteById(event.item.id));
        this.validateForm(this.maintenanceOrderNoteContract);
      } catch (error) {
        console.error('Error al consultar la Nota / Etapa por Id.', error);
      }
    }
    if (event.action === 'delete' && event.item) {
      if (this.maintenanceOrderStateId === MaintenanceOrderEnums.cierreFinal || this.swBlock) {
        this.toastHandlerService.showToast('error', 'No se puede eliminar una nota de etapa en una orden de mantenimiento en cierre final o bloqueada.');
        return;
      }
      const canUpdate = await this.validatePermissionUpdatable();
      if (!canUpdate) return;
      if (event.item.consecutive === 1) {
        this.toastHandlerService.showToast('error', 'No se puede eliminar la Nota 1, es la Etapa inicial.');
        return;
      }
      try {
        const result = await Swal.fire({
          title: 'Atención',
          text: `¿Realmente desea eliminar la Nota / Etapa ${event.item.consecutive}?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Si',
          cancelButtonText: 'No'
        });
        if (result.isConfirmed) {
          try {
            await firstValueFrom(this.maintenanceOrderNoteService.deleteMaintenanceOrderNote(event.item.id));
            this.toastHandlerService.showToast('success', 'La Nota / Etapa fue eliminada exitosamente.');
            this.changeLastNoteStage = true;
            this.getAllMaintenanceOrderNotesWithPagination();
          } catch (error) {
            console.error('Error al eliminar la Orden de Mantenimiento.', error);
          }
        }
      } catch (error) {
        console.error('Error al eliminar la Nota / Etapa.', error);
      }
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getAllMaintenanceOrderNotesWithPagination();
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async send() {
    if (this.maintenanceOrderStateId === MaintenanceOrderEnums.cierreFinal || this.swBlock) {
      this.toastHandlerService.showToast('error', 'No se puede crear o modificar una nota de etapa en una orden de mantenimiento en cierre final o bloqueada.');
      return;
    }
    const canUpdate = await this.validatePermissionUpdatable();
    if (!canUpdate) return;
    Object.keys(this.formNoteStage.controls).forEach(key => {
      const control = this.formNoteStage.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, control.value);
      }
    });
    if (this.formNoteStage.valid) {
      //Habilito el formulario para poder obtener los datos
      this.formNoteStage.enable();
      const modelMaintenanceOrderNote = this.formNoteStage.value as MaintenanceOrderNoteModel;
      //Hago null, los campos que permiten ser nulos:
      if (modelMaintenanceOrderNote.annotation && modelMaintenanceOrderNote.annotation.trim() === "") modelMaintenanceOrderNote.annotation = null;

      if (this.maintenanceOrderNoteContract.maintenanceOrderNoteId === 0) {
        //Asigno los valores en las variables fuera del formulario
        modelMaintenanceOrderNote.maintenanceOrderId = this.maintenanceOrderId;
        try {
          this.maintenanceOrderNoteContract = await firstValueFrom(this.maintenanceOrderNoteService.createMaintenanceOrderNote(modelMaintenanceOrderNote));
          this.validateForm(this.maintenanceOrderNoteContract);
          this.toastHandlerService.showToast('success', 'La Nota / Etapa fue creada exitosamente.');
          this.changeLastNoteStage = true;
          this.getAllMaintenanceOrderNotesWithPagination();
        } catch (error) {
          console.error('Error al crear la Nota / Etapa.', error);
        }
      } else {
        this.maintenanceOrderNoteContract.conceptId = modelMaintenanceOrderNote.conceptId;
        this.maintenanceOrderNoteContract.dateMaintenanceOrderNote = modelMaintenanceOrderNote.dateMaintenanceOrderNote;
        this.maintenanceOrderNoteContract.annotation = modelMaintenanceOrderNote.annotation;
        try {
          this.maintenanceOrderNoteContract = await firstValueFrom(this.maintenanceOrderNoteService.updateMaintenanceOrderNote(this.maintenanceOrderNoteContract));
          this.toastHandlerService.showToast('success', 'La Nota / Etapa fue modificada exitosamente.');
          this.changeLastNoteStage = true;
          this.newNoteStage();
          this.getAllMaintenanceOrderNotesWithPagination();
        } catch (error) {
          console.error('Error al actualizar la Nota / Etapa.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: any = {
      conceptId: 'El campo Etapa es obligatorio.',
      dateMaintenanceOrderNote: 'El campo Fecha es obligatorio.',
    };
    Object.keys(controlMessages).forEach(control => {
      if (this.formNoteStage.controls[control].invalid) {
        this.toastHandlerService.showToast('warning', controlMessages[control]);
      }
    });
  }

  validateForm(maintenanceOrderNote: MaintenanceOrderNoteModel) {
    this.formNoteStage = this.formBuilder.group({
      consecutive: [maintenanceOrderNote.consecutive],
      conceptId: [maintenanceOrderNote.conceptId, [Validators.required, Validators.min(1)]],
      dateMaintenanceOrderNote: [maintenanceOrderNote.dateMaintenanceOrderNote ?
        this.dateConverterService.convertUtcToLocalDate(maintenanceOrderNote.dateMaintenanceOrderNote) : null, [Validators.required]],
      annotation: [maintenanceOrderNote.annotation, [Validators.maxLength(500)]],
    });
    setTimeout(() => { this.formNoteStage.controls['consecutive'].disable() }, 100);
  }

  async validatePermissionUpdatable(): Promise<boolean> {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', 'El usuario no tiene permisos para modificar Notas / Etapas. Comuníquese con el administrador del sistema.');
      return false;
    }
    return true;
  }

}
